import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type Report, type ReportEvent, type ReportStatus, type EphemeralEnv } from '../api';
import { useAuth } from '../auth';
import { DiffView } from '../ui';

/** Diff do PR do report (carrega sob demanda — fetch + git diff pode demorar). */
function ReportDiff({ slug, reportId }: { slug: string; reportId: string }) {
  const [diff, setDiff] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [format, setFormat] = useState<'side-by-side' | 'line-by-line'>('line-by-line');

  function load() {
    setLoading(true);
    setErr(null);
    api
      .reportDiff(slug, reportId)
      .then(({ diff }) => setDiff(diff))
      .catch((e) => setErr((e as { error?: string }).error === 'no_branch' ? 'Report sem branch ainda.' : 'Falha ao carregar o diff.'))
      .finally(() => setLoading(false));
  }

  return (
    <div className="box">
      <div className="report-diff-head">
        <h3>Diff do PR</h3>
        <div className="report-diff-actions">
          {diff !== null && (
            <button className="btn-ghost-sm" onClick={() => setFormat((f) => (f === 'line-by-line' ? 'side-by-side' : 'line-by-line'))}>
              {format === 'line-by-line' ? 'lado a lado' : 'em linha'}
            </button>
          )}
          <button className="btn-ghost-sm" onClick={load} disabled={loading}>
            {loading ? 'carregando…' : diff === null ? 'ver diff' : 'recarregar'}
          </button>
        </div>
      </div>
      {err && <div className="chat-error">{err}</div>}
      {diff !== null && <DiffView diff={diff} outputFormat={format} />}
    </div>
  );
}

function EventItem({ event, defaultOpen = false }: { event: ReportEvent; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const payload = event.payload ?? {};
  const prompt = typeof payload['prompt'] === 'string' ? (payload['prompt'] as string) : null;
  const sessionSummary = typeof payload['sessionSummary'] === 'string' ? (payload['sessionSummary'] as string) : null;
  const otherKeys = Object.keys(payload).filter((k) => k !== 'prompt' && k !== 'sessionSummary');
  const actor = event.actorLabel ?? (event.actorUserId ? 'usuário' : 'sistema');

  const hasDetails = prompt !== null || sessionSummary !== null || otherKeys.length > 0;

  return (
    <div className="evt">
      <div className="evt-row">
        <strong>{event.type}</strong>
        <span className="evt-meta">
          {actor} · {new Date(event.createdAt).toLocaleString()}
        </span>
        {hasDetails && (
          <button type="button" className="link evt-toggle" onClick={() => setOpen((v) => !v)}>
            {open ? 'ocultar' : 'detalhes'}
          </button>
        )}
      </div>
      {open && hasDetails && (
        <div className="evt-details">
          {otherKeys.length > 0 && (
            <table className="evt-kv">
              <tbody>
                {otherKeys.map((k) => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td>
                      <code>{formatValue(payload[k])}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {prompt && (
            <details className="evt-code" open>
              <summary>prompt enviado ao Claude CLI ({prompt.length.toLocaleString()} chars)</summary>
              <pre>{prompt}</pre>
              <button
                type="button"
                className="link"
                onClick={() => navigator.clipboard.writeText(prompt)}
              >
                copiar
              </button>
            </details>
          )}
          {sessionSummary && (
            <details className="evt-code">
              <summary>timeline da sessão ({sessionSummary.length.toLocaleString()} chars)</summary>
              <pre>{sessionSummary}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

const PIPE_TYPES = new Set([
  'dispatched', 'triaged', 'pipeline_stage', 'review_started', 'review_result',
  'changes_requested', 'pr_created', 'deploy_started', 'deploy_finished', 'rejected', 'error',
]);

function pipelineLabel(e: ReportEvent): { icon: string; label: string; detail?: string } {
  const p = e.payload ?? {};
  const s = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : '');
  switch (e.type) {
    case 'dispatched': return { icon: '📨', label: 'Despachado' };
    case 'triaged': return { icon: '🧭', label: 'Triagem concluída', detail: s('area') && `área: ${s('area')}` };
    case 'pipeline_stage': return { icon: '▶️', label: `Etapa: ${s('stage') || s('role') || '—'}`, detail: [s('area') && `área ${s('area')}`, p['iteration'] ? `iteração ${p['iteration']}` : ''].filter(Boolean).join(' · ') };
    case 'review_started': return { icon: '🔍', label: `Revisão: ${s('role') || '—'}` };
    case 'review_result': {
      const v = s('verdict');
      const icon = v === 'approved' ? '✅' : v === 'rejected' ? '⛔' : '✏️';
      const findings = Array.isArray(p['findings']) ? (p['findings'] as unknown[]).length : 0;
      return { icon, label: `${s('role') || 'revisor'}: ${v || 'revisado'}`, detail: [s('summary'), findings ? `${findings} achado(s)` : ''].filter(Boolean).join(' — ') };
    }
    case 'changes_requested': return { icon: '✏️', label: 'Mudanças pedidas' };
    case 'pr_created': return { icon: '✅', label: 'PR aberto', detail: s('url') };
    case 'deploy_started': return { icon: '🚀', label: 'Deploy iniciado' };
    case 'deploy_finished': return { icon: '🏁', label: 'Deploy concluído', detail: s('status') };
    case 'rejected': return { icon: '⛔', label: 'Rejeitado', detail: s('reason') };
    case 'error': return { icon: '❌', label: 'Erro', detail: s('message') };
    default: return { icon: '•', label: e.type };
  }
}

/** Timeline das etapas do pipeline (lê os eventos já carregados; sem API extra). */
function PipelineTimeline({ events }: { events: ReportEvent[] }) {
  const items = events.filter((e) => PIPE_TYPES.has(e.type));
  if (items.length === 0) return null;
  return (
    <div className="box">
      <h3>Timeline do pipeline</h3>
      <ul className="pipe-timeline">
        {items.map((e) => {
          const { icon, label, detail } = pipelineLabel(e);
          return (
            <li key={e.id} className="pipe-ev">
              <span className="pipe-ev-icon" aria-hidden>{icon}</span>
              <div className="pipe-ev-main">
                <div className="pipe-ev-label">{label}</div>
                {detail && <div className="pipe-ev-detail">{detail}</div>}
                <div className="pipe-ev-time">{e.actorLabel ?? 'sistema'} · {new Date(e.createdAt).toLocaleString()}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ClaudeTrail({ events }: { events: ReportEvent[] }) {
  const progress = events.filter((e) => e.type === 'progress');
  if (progress.length === 0) return null;

  const init = progress.find((e) => (e.payload?.['kind'] ?? null) === 'runtime_init');
  const mcpServers = (init?.payload?.['mcpServers'] as Array<Record<string, unknown>> | null) ?? null;
  const tools = (init?.payload?.['tools'] as string[] | null) ?? null;
  const model = (init?.payload?.['model'] as string | null) ?? null;
  const finished = progress.find((e) => (e.payload?.['kind'] ?? null) === 'run_finished');

  return (
    <div className="box">
      <h3>Trilha do Claude</h3>
      {init && (
        <div className="trail-init">
          <div className="trail-init-row">
            <strong>modelo</strong>
            <span>{model ?? '—'}</span>
          </div>
          {mcpServers && mcpServers.length > 0 && (
            <div className="trail-init-row">
              <strong>MCP</strong>
              <span>
                {mcpServers.map((s, i) => {
                  const name = String(s['name'] ?? '?');
                  const status = String(s['status'] ?? '?');
                  const ok = status === 'connected' || status === 'ok';
                  return (
                    <span key={i} className={`trail-mcp trail-mcp-${ok ? 'ok' : 'err'}`}>
                      {name}:{status}
                    </span>
                  );
                })}
              </span>
            </div>
          )}
          {tools && tools.length > 0 && (
            <div className="trail-init-row">
              <strong>tools</strong>
              <span className="trail-tools">{tools.join(' · ')}</span>
            </div>
          )}
        </div>
      )}
      <ol className="trail-steps">
        {progress
          .filter((e) => {
            const kind = e.payload?.['kind'];
            return kind === 'tool_use' || kind === 'assistant_text';
          })
          .map((e) => (
            <TrailStep key={e.id} event={e} />
          ))}
      </ol>
      {finished && (
        <div className="trail-finished">
          ✓ concluído — {String(finished.payload?.['numTurns'] ?? '?')} turnos ·{' '}
          {formatDuration(finished.payload?.['durationMs'] as number | undefined)}
          {finished.payload?.['costUsd'] != null && (
            <> · US$ {Number(finished.payload['costUsd']).toFixed(4)}</>
          )}
        </div>
      )}
    </div>
  );
}

function TrailStep({ event }: { event: ReportEvent }) {
  const [open, setOpen] = useState(false);
  const kind = event.payload?.['kind'] as string | undefined;
  const at = new Date(event.createdAt).toLocaleTimeString();

  if (kind === 'tool_use') {
    const tool = String(event.payload?.['tool'] ?? '?');
    const input = event.payload?.['input'] as Record<string, unknown> | null | undefined;
    const summary = toolSummary(tool, input);
    const expanded = input ? JSON.stringify(input, null, 2) : null;
    return (
      <li className="trail-step">
        <span className="trail-at">{at}</span>
        <span className={`trail-tool trail-tool-${tool.toLowerCase()}`}>{tool}</span>
        <span className="trail-summary">{summary}</span>
        {expanded && (
          <button type="button" className="link trail-toggle" onClick={() => setOpen((v) => !v)}>
            {open ? '×' : '…'}
          </button>
        )}
        {open && expanded && <pre className="trail-expanded">{expanded}</pre>}
      </li>
    );
  }

  if (kind === 'assistant_text') {
    const text = String(event.payload?.['text'] ?? '');
    return (
      <li className="trail-step trail-step-text">
        <span className="trail-at">{at}</span>
        <span className="trail-tool trail-tool-text">Claude</span>
        <span className="trail-summary trail-text-body">{text}</span>
      </li>
    );
  }

  return null;
}

function toolSummary(tool: string, input: Record<string, unknown> | null | undefined): string {
  if (!input) return '';
  const t = tool.toLowerCase();
  const file = (input['file_path'] ?? input['path']) as string | undefined;
  if ((t === 'read' || t === 'edit' || t === 'write' || t === 'notebookedit') && file) return file;
  if (t === 'grep') {
    const p = String(input['pattern'] ?? '');
    const g = input['glob'] ? ` in ${input['glob']}` : input['path'] ? ` in ${input['path']}` : '';
    return `${truncateOneLine(p, 80)}${g}`;
  }
  if (t === 'glob') return String(input['pattern'] ?? '');
  if (t === 'bash') return truncateOneLine(String(input['command'] ?? ''), 140);
  if (t === 'webfetch') return String(input['url'] ?? '');
  if (t === 'websearch') return String(input['query'] ?? '');
  if (t === 'task' || t === 'agent') return truncateOneLine(String(input['description'] ?? ''), 120);
  if (t === 'todowrite') return 'atualizou todo list';
  // fallback: first string-ish value, truncated
  const firstString = Object.values(input).find((v) => typeof v === 'string') as string | undefined;
  return firstString ? truncateOneLine(firstString, 120) : '';
}

function truncateOneLine(s: string, n: number): string {
  const oneLine = s.replace(/\s+/g, ' ').trim();
  return oneLine.length > n ? oneLine.slice(0, n - 1) + '…' : oneLine;
}

function formatDuration(ms?: number): string {
  if (ms == null) return '?';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}s`;
}

function LiveStage({
  report,
  events,
  now,
}: {
  report: Report;
  events: ReportEvent[];
  now: number;
}) {
  const s = report.status;
  if (s === 'resolved' || s === 'rejected') return null;

  let label: string;
  let anchorTs: number;

  if (s === 'queued') {
    label = 'Aguardando triagem';
    anchorTs = new Date(report.createdAt).getTime();
  } else if (s === 'triaging') {
    const evt = [...events].reverse().find(
      (e) => e.type === 'status_changed' && (e.payload?.['stage'] === 'triage' || e.payload?.['to'] === 'triaging'),
    );
    label = 'Claude triando…';
    anchorTs = evt ? new Date(evt.createdAt).getTime() : new Date(report.updatedAt).getTime();
  } else if (s === 'dispatched') {
    const evt = [...events].reverse().find((e) => e.type === 'dispatched');
    const mode = report.lastTesterNote ? 'aplicando retorno do tester' : report.ciStatus === 'failed' ? 'corrigindo CI' : 'implementando';
    label = evt ? `Claude ${mode}…` : 'Na fila para implementação';
    anchorTs = evt ? new Date(evt.createdAt).getTime() : new Date(report.updatedAt).getTime();
  } else if (s === 'pr_open') {
    if (report.ciStatus === 'passed') return null;  // terminal local: dono clica deploy
    if (report.ciStatus === 'failed') {
      label = 'CI falhou — aguardando auto-fix';
      anchorTs = new Date(report.updatedAt).getTime();
    } else {
      label = 'Aguardando CI do GitHub…';
      anchorTs = new Date(report.updatedAt).getTime();
    }
  } else if (s === 'deploying') {
    if (report.deployStatus === 'running') {
      label = 'Manual deploy rodando no GitHub Actions…';
      const ev = [...events].reverse().find((e) => e.type === 'deploy_started');
      anchorTs = ev ? new Date(ev.createdAt).getTime() : new Date(report.updatedAt).getTime();
    } else {
      label = 'Disparando deploy…';
      anchorTs = new Date(report.updatedAt).getTime();
    }
  } else if (s === 'in_review') {
    label = 'Deploy pronto — aguardando tester validar';
    anchorTs = new Date(report.updatedAt).getTime();
  } else {
    return null;
  }

  return (
    <div className="live-stage">
      <span className="live-dot" />
      <span className="live-label">{label}</span>
      <span className="live-timer">{formatElapsed(now - anchorTs)}</span>
    </div>
  );
}

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m${String(rem).padStart(2, '0')}s`;
}

function TriagePanel({
  verdict,
  notes,
  canDispatch,
  onDispatch,
  busy,
}: {
  verdict: string;
  notes: string | null;
  canDispatch: boolean;
  onDispatch: () => void;
  busy: boolean;
}) {
  const isValid = verdict === 'valid';
  const isReject = verdict === 'reject' || verdict === 'duplicate' || verdict === 'unclear';
  const flavor = isValid ? 'ok' : isReject ? 'warn' : 'error';
  const label =
    verdict === 'valid' ? 'Triagem: acionável'
    : verdict === 'reject' ? 'Triagem: rejeitado'
    : verdict === 'duplicate' ? 'Triagem: duplicado'
    : verdict === 'unclear' ? 'Triagem: pouco claro'
    : `Triagem: ${verdict}`;

  return (
    <div className={`triage-panel triage-${flavor}`}>
      <div className="triage-head">
        <strong>{label}</strong>
      </div>
      {notes && <div className="triage-body">{notes}</div>}
      {canDispatch && (
        <div className="triage-actions">
          <button type="button" className="primary" disabled={busy} onClick={onDispatch}>
            Enviar ao Claude mesmo assim
          </button>
        </div>
      )}
    </div>
  );
}

const TRANSITIONS: Array<{ to: ReportStatus; label: string; style?: 'primary' | 'danger' }> = [
  { to: 'triaging', label: 'Em triagem' },
  { to: 'dispatched', label: 'Enviar ao Claude', style: 'primary' },
  { to: 'resolved', label: 'Resolvido' },
  { to: 'rejected', label: 'Rejeitar', style: 'danger' },
];

export function ReportDetail() {
  const { slug, reportId } = useParams<{ slug: string; reportId: string }>();
  const { me } = useAuth();
  const [report, setReport] = useState<Report | null>(null);
  const [events, setEvents] = useState<ReportEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ephMsg, setEphMsg] = useState('');
  const [eph, setEph] = useState<EphemeralEnv | null>(null); // efêmero ativo da branch deste report
  const [now, setNow] = useState(Date.now());
  const playerRef = useRef<HTMLDivElement>(null);

  const canMaintain = me?.globalRole === 'admin'; // v2: resolve project role

  // Atualiza o contador do indicador "ao vivo" a cada segundo, sem bater na API.
  useEffect(() => {
    if (!report) return;
    if (report.status === 'resolved' || report.status === 'rejected') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [report?.status]);

  useEffect(() => {
    if (!slug || !reportId) return;
    let alive = true;
    const load = () =>
      api
        .getReport(slug, reportId)
        .then(async ({ report, events }) => {
          if (!alive) return;
          setReport(report);
          setEvents(events);
          if (report.branchName) {
            try {
              const { environments } = await api.listEphemeral(slug);
              if (alive) setEph(environments.find((e) => e.branch === report.branchName && e.status !== 'destroyed') ?? null);
            } catch {
              /* ignore */
            }
          }
        })
        .catch(() => alive && setErr('Falha ao carregar report'));
    load();
    // Poll até o card chegar em estado final. pr_open e in_review ainda polam
    // porque CI / tester podem promover automaticamente.
    const t = setInterval(() => {
      if (!alive) return;
      const s = report?.status;
      if (s === 'resolved' || s === 'rejected') return;
      load();
    }, 3000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [slug, reportId, report?.status]);

  async function changeStatus(to: ReportStatus) {
    if (!slug || !reportId) return;
    setBusy(true);
    try {
      const { report } = await api.updateStatus(slug, reportId, to);
      setReport(report);
      const { events } = await api.getReport(slug, reportId);
      setEvents(events);
    } finally {
      setBusy(false);
    }
  }

  async function deployToDev() {
    if (!slug || !reportId) return;
    setBusy(true);
    try {
      const { report } = await api.deploy(slug, reportId, 'dev');
      setReport(report);
      const { events } = await api.getReport(slug, reportId);
      setEvents(events);
    } finally {
      setBusy(false);
    }
  }

  async function openPrNow() {
    if (!slug || !reportId) return;
    setBusy(true);
    try {
      await api.openReportPr(slug, reportId);
      const { report, events } = await api.getReport(slug, reportId);
      setReport(report);
      setEvents(events);
    } finally {
      setBusy(false);
    }
  }

  // Sobe um efêmero da branch deste report (sem precisar voltar pro chat/ops).
  async function subirAmbiente() {
    if (!slug || !report?.branchName) return;
    setBusy(true);
    setEphMsg('');
    try {
      await api.createEphemeral(slug, { branch: report.branchName, mode: 'manual' });
      setEphMsg('🚀 Subindo (1º build leva alguns minutos)…');
    } catch (e) {
      const c = (e as { error?: string }).error;
      setEphMsg(c === 'no_free_port' ? 'Sem porta livre.' : c === 'ephemeral_not_configured' ? 'Efêmero não configurado neste projeto.' : 'Falha ao subir o ambiente.');
    } finally {
      setBusy(false);
    }
  }

  // Atualiza o efêmero existente da branch no lugar (puxa o commit novo, mesma URL).
  async function atualizarAmbiente() {
    if (!slug || !eph) return;
    setBusy(true);
    setEphMsg('');
    try {
      await api.redeployEphemeral(slug, eph.id);
      setEphMsg('🔄 Atualizando o ambiente…');
    } catch {
      setEphMsg('Falha ao atualizar o ambiente.');
    } finally {
      setBusy(false);
    }
  }

  async function sendTesterFeedback(ok: boolean) {
    if (!slug || !reportId) return;
    let note: string | undefined;
    if (!ok) {
      const input = prompt('O que deu errado? (vai pro Claude corrigir)');
      if (!input || input.trim().length < 3) return;
      note = input.trim();
    }
    setBusy(true);
    try {
      const { report } = await api.testerFeedback(slug, reportId, ok, note);
      setReport(report);
      const { events } = await api.getReport(slug, reportId);
      setEvents(events);
    } finally {
      setBusy(false);
    }
  }

  async function playReplay() {
    if (!slug || !reportId || !playerRef.current) return;
    const { url } = await api.artifactUrl(slug, reportId, 'session');
    const res = await fetch(url);
    const b64 = await res.text(); // artifact is base64 of JSON (gzip MIME but we stored raw b64)
    const json = atob(b64);
    const events = JSON.parse(json);
    playerRef.current.innerHTML = '';
    const { default: Player } = await import('rrweb-player');
    // eslint-disable-next-line @typescript-eslint/no-new
    new Player({ target: playerRef.current, props: { events, autoPlay: true, width: 800, height: 450 } });
  }

  if (err) return <div>{err}</div>;
  if (!report) return <div>Carregando…</div>;

  return (
    <div className="detail">
      <div className="main">
        <h1 style={{ marginTop: 0 }}>{report.title}</h1>
        <div style={{ color: 'var(--color-fg-muted)', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`ui-badge ui-badge-${report.kind === 'bug' ? 'bug' : report.kind === 'scenario' ? 'scenario' : 'suggestion'}`}>{report.kind}</span>
          <span className="ui-badge ui-badge-outline">{report.status}</span>
          <span>{new Date(report.createdAt).toLocaleString()}</span>
        </div>
        <LiveStage report={report} events={events} now={now} />
        <div className="desc">{report.description}</div>
        {report.pageUrl && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
            URL: <code>{report.pageUrl}</code>
            {report.releaseVersion && ` · versão ${report.releaseVersion}`}
          </div>
        )}
        {report.triageVerdict && (
          <TriagePanel
            verdict={report.triageVerdict}
            notes={report.triageNotes}
            canDispatch={canMaintain && report.status !== 'dispatched' && report.status !== 'pr_open'}
            onDispatch={() => changeStatus('dispatched')}
            busy={busy}
          />
        )}
        <div ref={playerRef} style={{ marginTop: 16 }} />
        {report.sessionArtifactKey && canMaintain && (
          <button
            type="button"
            className="link"
            onClick={playReplay}
            style={{ marginTop: 8 }}
          >
            ▶ tocar replay
          </button>
        )}
      </div>
      <div className="side">
        {canMaintain && (
          <div className="box">
            <h3>Ações</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Branch commitada, sem PR: valide no efêmero e abra o PR quando aprovar */}
              {report.status === 'committed' && (
                <>
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                    ✅ Código commitado na branch{report.branchName ? ` "${report.branchName}"` : ''} — sem PR ainda.
                    Suba o efêmero dessa branch, valide, e então abra o PR:
                  </div>
                  {eph?.status === 'running' && eph.url ? (
                    <div style={{ fontSize: 13 }}>
                      🌐 <a href={eph.url} target="_blank" rel="noreferrer">Acessar ambiente →</a>{' · '}
                      <button type="button" disabled={busy} onClick={atualizarAmbiente}>🔄 Atualizar</button>{' · '}
                      <Link to={`/p/${slug}/ephemeral`}>Efêmeros</Link>
                    </div>
                  ) : eph?.status === 'creating' ? (
                    <span style={{ fontSize: 13, color: 'var(--muted)' }}>⏳ Ambiente subindo… (1º build leva alguns minutos) <Link to={`/p/${slug}/ephemeral`}>Efêmeros →</Link></span>
                  ) : (
                    <button type="button" disabled={busy || !report.branchName} onClick={subirAmbiente}>
                      🚀 Subir ambiente desta branch
                    </button>
                  )}
                  {ephMsg && <span style={{ fontSize: 13, color: 'var(--muted)' }}>{ephMsg}</span>}
                  <button type="button" className="primary" disabled={busy || report.openPrRequested} onClick={openPrNow}>
                    {report.openPrRequested ? 'Abrindo PR…' : '✅ Validei — abrir o PR'}
                  </button>
                </>
              )}
              {/* Deploy: só quando tem PR e não está em deploy/review */}
              {report.prUrl && report.status === 'pr_open' && (
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={deployToDev}
                  title={
                    report.ciStatus === 'passed'
                      ? 'CI verde — pronto pra deploy'
                      : 'CI ainda não passou. Deploy vai ser feito mesmo assim.'
                  }
                >
                  {report.ciStatus === 'passed'
                    ? 'Deploy pra VPS (dev)'
                    : report.ciStatus === 'failed'
                    ? 'Deploy mesmo assim (CI ✕)'
                    : 'Deploy pra VPS (CI ainda rodando)'}
                </button>
              )}
              {/* Tester feedback */}
              {report.status === 'in_review' && (
                <>
                  <button type="button" className="primary" disabled={busy} onClick={() => sendTesterFeedback(true)}>
                    ✓ Testado OK — aceitar
                  </button>
                  <button type="button" className="danger" disabled={busy} onClick={() => sendTesterFeedback(false)}>
                    ✕ Testado com problemas — devolver ao Claude
                  </button>
                </>
              )}
              {/* Transições manuais (mantidas pra admin usar em casos de canto) */}
              {TRANSITIONS.map((t) => (
                <button
                  key={t.to}
                  type="button"
                  className={t.style ?? ''}
                  disabled={busy || report.status === t.to}
                  onClick={() => changeStatus(t.to)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {report.prUrl && (
          <div className="box">
            <h3>Pull Request</h3>
            <a href={report.prUrl} target="_blank" rel="noreferrer">
              #{report.prNumber} — ver no GitHub
            </a>
            {report.ciStatus && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                CI:{' '}
                <span className={`ci-badge ci-badge-${report.ciStatus}`}>
                  {report.ciStatus === 'passed' ? '✓ passou' : report.ciStatus === 'failed' ? '✕ falhou' : '… rodando'}
                </span>
                {report.ciRunUrl && (
                  <>
                    {' · '}
                    <a href={report.ciRunUrl} target="_blank" rel="noreferrer">
                      ver checks
                    </a>
                  </>
                )}
                {report.autoFixAttempts > 0 && (
                  <span style={{ marginLeft: 8, color: 'var(--muted)' }}>
                    auto-fix ×{report.autoFixAttempts}
                  </span>
                )}
              </div>
            )}
            {report.deployRunUrl && (
              <div style={{ marginTop: 6, fontSize: 12 }}>
                Deploy:{' '}
                <a href={report.deployRunUrl} target="_blank" rel="noreferrer">
                  {report.deployStatus ?? 'ver run'}
                </a>
              </div>
            )}
          </div>
        )}
        <PipelineTimeline events={events} />
        {report.branchName && <ReportDiff slug={slug!} reportId={report.id} />}
        <ClaudeTrail events={events} />
        <div className="box">
          <h3>Histórico</h3>
          <div className="events">
            {events
              .filter((e) => e.type !== 'progress')
              .map((e) => (
                <EventItem
                  key={e.id}
                  event={e}
                  defaultOpen={e.type === 'triage_result' || e.type === 'error'}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
